import { useEffect, useReducer } from 'react';

import useDropdown from '../usedropdown';

import reducer from './reducer';
import * as ACTIONS from './actions';

const noop = () => {};
const toFakeEvent = x => ({
  target: { value: x },
  preventDefault: noop,
  stopPropagation: noop
});

export const CRITERIA_ITEM_TYPES = {
  WORKFLOW_STEP: 'WORKFLOW_STEP',
  AUDITED_BY: 'AUDITED_BY',
  FINALIZED_BY: 'FINALIZED_BY',
  ONHOLD_BY: 'ONHOLD_BY'
};

const reviewSearchInitialState = {
  isLoading: false,
  statusMessage: '',
  inputs: {
    text: {
      mrn: '',
      accountNumber: ''
    },
    date: {
      dosStart: '',
      dosEnd: '',
      finalizedOnStart: '',
      finalizedOnEnd: '',
      auditedOnStart: '',
      auditedOnEnd: ''
    }
  },
  selectedCriteria: {
    [CRITERIA_ITEM_TYPES.WORKFLOW_STEP]: [],
    [CRITERIA_ITEM_TYPES.AUDITED_BY]: [],
    [CRITERIA_ITEM_TYPES.FINALIZED_BY]: [],
    [CRITERIA_ITEM_TYPES.ONHOLD_BY]: []
  },
  searchResults: []
};

const useReviewSearch = service => {
  const [state, dispatch] = useReducer(reducer, reviewSearchInitialState);
  const [contractsDropdownState, contractsDropdownActions] = useDropdown();
  const [facilitiesDropdownState, facilitiesDropdownActions] = useDropdown();
  const [finalizedByDropdownState, finalizedByDropdownActions] = useDropdown();
  const [workflowDropdownState, workflowDropdownActions] = useDropdown();
  const [auditedByDropdownState, auditedByDropdownActions] = useDropdown();
  const [encounterHoldTypesDropdownState, encounterHoldTypeDrowndownActions] = useDropdown();
  const [encounterReviewStatusDropdownState, encounterReviewStatusDropdownActions] = useDropdown();
  const [onHoldByDropdownState, onHoldbyDropdownActions] = useDropdown();

  const withDispatchUnary = fn => x => dispatch(fn(x));
  const withDispatchBinary = fn => (x, y) => dispatch(fn(x, y));
  const withDispatchTres = fn => (x, y, z) => dispatch(fn(x, y, z));

  const selectCriteriaItem = withDispatchBinary(ACTIONS.selectCriteriaItem);
  const removeCriteriaItem = withDispatchBinary(ACTIONS.removeCriteriaItem);

  const changeInputValue = withDispatchTres(ACTIONS.changeInputValue);

  const search = dto => {
    dispatch(ACTIONS.searchRequest());

    return service.search(dto)
      .then(result => {
        dispatch(ACTIONS.searchSuccess(result));
      })
      .catch(e => dispatch(ACTIONS.searchFailure(e.toString())));
  };

  const navigateToDetails = (encounterId, doRedirect) => {
    dispatch(ACTIONS.navigateToDetails(encounterId));

    doRedirect(service.serviceRoutes.redirectToDetails(encounterId));
  };

  const getCodersForContract = contractId => {
    dispatch(ACTIONS.getCodersRequest());

    return service.getCodersForContract(contractId)
      .then(result => {
        finalizedByDropdownActions.setItems(result.finalizedByOptions);
        auditedByDropdownActions.setItems(result.auditedByOptions);
        onHoldbyDropdownActions.setItems(result.onHoldByOptions);
        dispatch(ACTIONS.getCodersSuccess());
      })
      .catch(e => dispatch(ACTIONS.getCodersFailure(e.toString())));
  };

  const setContractAndUpdateCoders = e => {
    const contractId = e.target.value;

    return getCodersForContract(contractId)
      .then(() => {
        contractsDropdownActions.setSelectedItem(toFakeEvent(contractId));
      })
  };

  const boundActions = {
    setSelectedContract: setContractAndUpdateCoders,
    setSelectedFacility: facilitiesDropdownActions.setSelectedItem,
    setSelectedEncounterHoldOption: encounterHoldTypeDrowndownActions.setSelectedItem,
    setSelectedEncounterReviewStatusOption: encounterReviewStatusDropdownActions.setSelectedItem,
    selectCriteriaItem,
    removeCriteriaItem,
    changeInputValue,
    search,
    navigateToDetails,
    navigateToReviewDetails: service.serviceRoutes.redirectToDetails
  };

  // init
  useEffect(() => {
    dispatch(ACTIONS.initRequest());

    service.init()
      .then(result => {
        const {
          contracts,
          selectedContract,
          facilities,
          selectedFacility,
          workflowSteps,
          selectedWorkflowStep,
          finalizedByOptions,
          selectedFinalizedByOption,
          auditedByOptions,
          selectedAuditedByOption,
          encounterHoldTypeOptions,
          encounterHoldReviewStatusOptions,
          onHoldByOptions,
          selectedOnHoldByOption
        } = result;

        const mappedWorkflowSteps = workflowSteps.map(x => ({
          ...x,
          value: x.value + ''
        }));

        contractsDropdownActions.setItems(contracts);
        contractsDropdownActions.setSelectedItem(
          toFakeEvent(selectedContract.value)
        );

        facilitiesDropdownActions.setItems(facilities);
        facilitiesDropdownActions.setSelectedItem(
          toFakeEvent(selectedFacility.value)
        );

        finalizedByDropdownActions.setItems(finalizedByOptions);
        workflowDropdownActions.setItems(mappedWorkflowSteps);
        auditedByDropdownActions.setItems(auditedByOptions);
        onHoldbyDropdownActions.setItems(onHoldByOptions);
        encounterHoldTypeDrowndownActions.setItems(encounterHoldTypeOptions);
        encounterReviewStatusDropdownActions.setItems(encounterHoldReviewStatusOptions);

        dispatch(ACTIONS.initSuccess(result));
      })
      .catch(e => dispatch(ACTIONS.initFailure(e.toString())));
  }, []);

  const isPageLoading = (
    state.isLoading
  );

  const getStatusMessage = () => {
    const hasMessage = s => s.statusMessage.length > 0;

    return state.statusMessage;
  };

  return [
    {
      ...state,
      isLoading: isPageLoading,
      statusMessage: getStatusMessage(),
      contracts: { ...contractsDropdownState },
      facilities: { ...facilitiesDropdownState },
      workflow: { ...workflowDropdownState },
      finalizedBy: { ...finalizedByDropdownState },
      auditedBy: { ...auditedByDropdownState },
      onHoldBy: { ...onHoldByDropdownState },
      encounterHoldOptions: { ...encounterHoldTypesDropdownState },
      encounterReviewStatusOptions: { ...encounterReviewStatusDropdownState }
    },
    boundActions
  ];
};

export default useReviewSearch;
